import { useMemo, useState } from 'react';
import { View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useListedCars } from '@/api/partners/cars/use-listed-cars';
import { getCarImages } from '@/core';
import { useDebounce } from '@/core/hooks/use-debounced-value';
import Dropdown from '@/ui/dropdown';

export const DASHBOARD_PERIODS = [
  { id: '12', value: '12', title: 'Last 12 months' },
  { id: '6', value: '6', title: 'Last 6 months' },
  { id: '3', value: '3', title: 'Last 3 months' },
];

export function getDashboardPeriodLabel(monthCount: number) {
  return (
    DASHBOARD_PERIODS.find((period) => Number(period.value) === monthCount)
      ?.title ?? `Last ${monthCount} months`
  );
}

type DashboardFiltersProps = {
  monthCount: number;
  onMonthCountChange: (monthCount: number) => void;
  carId?: number;
  onCarIdChange?: (carId: number | undefined) => void;
  showCarFilter?: boolean;
};

export function DashboardFilters({
  monthCount,
  onMonthCountChange,
  carId,
  onCarIdChange,
  showCarFilter = false,
}: DashboardFiltersProps) {
  const { styles } = useStyles(stylesheet);
  const isCompactLayout = ['xs', 'sm'].includes(UnistylesRuntime.breakpoint);
  const [carSearchText, setCarSearchText] = useState('');
  const debouncedCarSearch = useDebounce(carSearchText, 400);
  const listedCarsSearch = debouncedCarSearch.trim();
  const listedCars = useListedCars({
    text: listedCarsSearch ? listedCarsSearch : undefined,
  });
  const listedCarsPage = listedCars.data?.pages[0]?.data;
  const carOptions = useMemo(() => {
    const cars = listedCarsPage ?? [];
    const options = [
      { id: 'all', value: 'all', title: 'All cars' },
      ...cars.map((car) => ({
        id: String(car.id),
        value: String(car.id),
        title: `${car.vehicleYear} ${car.brandName} ${car.modelName} (${car.licensePlate})`,
        imageUrl: getCarImages(car.images, 'small')[0]?.url,
      })),
    ];

    if (!carId) {
      return options;
    }

    const selectedValue = String(carId);

    if (options.some((option) => option.value === selectedValue)) {
      return options;
    }

    return [
      ...options,
      {
        id: selectedValue,
        value: selectedValue,
        title: `Car #${selectedValue}`,
      },
    ];
  }, [carId, listedCarsPage]);

  const periodFilter = (
    <Dropdown
      appearance="filter"
      options={DASHBOARD_PERIODS}
      placeHolderText="Last 12 months"
      values={[String(monthCount)]}
      onSelect={(selected) => {
        onMonthCountChange(Number(selected[0]?.value ?? 12));
      }}
      style={styles.filter}
    />
  );

  const carFilter =
    showCarFilter && onCarIdChange ? (
      <Dropdown
        appearance="filter"
        filterSearchable
        options={carOptions}
        placeHolderText="All cars"
        values={[carId ? String(carId) : 'all']}
        onFilterTextChange={setCarSearchText}
        onClose={() => {
          setCarSearchText('');
        }}
        onSelect={(selected) => {
          const value = selected[0]?.value;
          onCarIdChange(!value || value === 'all' ? undefined : Number(value));
        }}
        style={styles.filter}
        optionsContainerStyle={styles.carFilterMenu}
      />
    ) : null;

  return (
    <View style={styles.filters}>
      {isCompactLayout ? (
        <>
          {carFilter}
          {periodFilter}
        </>
      ) : (
        <>
          {periodFilter}
          {carFilter}
        </>
      )}
    </View>
  );
}

const stylesheet = createStyleSheet(() => ({
  filters: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    flex: {
      xs: 1,
      md: undefined,
    },
    minWidth: {
      xs: '100%',
      md: undefined,
    },
  },
  filter: {
    flex: {
      xs: 1,
      md: undefined,
    },
    minWidth: {
      xs: 0,
      md: 180,
    },
    width: {
      md: 180,
    },
  },
  carFilterMenu: {
    left: 'auto',
    right: 0,
    minWidth: 320,
  },
}));
